import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { api } from "@/lib/api";

const fmtDay = (d, loc) => new Date(d).toLocaleDateString(loc, { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "Europe/Berlin" });
const fmtTime = (d) => new Date(d).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });

export const WeekCoursesBanner = ({ lang }) => {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const scrollRef = useRef(null);
  const loc = lang === "de" ? "de-DE" : "en-US";
  const t = (de, en) => (lang === "de" ? de : en);

  useEffect(() => {
    api.get("/calendar").then(({ data }) => {
      const now = new Date();
      // Ende der aktuellen Kalenderwoche (Sonntag 23:59)
      const end = new Date(now);
      const day = (end.getDay() + 6) % 7; // Mo=0 … So=6
      end.setDate(end.getDate() + (6 - day));
      end.setHours(23, 59, 59, 999);
      const week = data
        .filter((e) => e.status !== "cancelled" && new Date(e.start) >= now && new Date(e.start) <= end)
        .sort((a, b) => a.start.localeCompare(b.start));
      setEvents(week);
    }).catch(() => {});
  }, []);

  if (events.length === 0) return null;

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  return (
    <section className="relative bg-[#0A0A0A] border-y border-[#ff0033]/30 overflow-hidden" data-testid="week-courses-banner">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 100% at 0% 50%, rgba(255,0,51,0.12) 0%, rgba(0,0,0,0) 60%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff0033] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#ff0033]" />
            </span>
            <h2 className="font-heading text-lg md:text-2xl uppercase tracking-tight text-white leading-none">
              {t("Kurse diese Woche:", "Courses this week:")}
            </h2>
            <span className="hidden sm:inline font-mono-lab text-[10px] uppercase tracking-widest text-[#ff0033] border border-[#ff0033]/40 px-2 py-1">
              {events.length} {t("Termine", "sessions")}
            </span>
          </div>
          <div className="hidden md:flex gap-1">
            <button onClick={() => scroll(-1)} aria-label="Zurück" data-testid="week-banner-prev" className="p-2 border border-white/15 text-white hover:border-[#ff0033] transition-colors"><ChevronLeft size={15} /></button>
            <button onClick={() => scroll(1)} aria-label="Weiter" data-testid="week-banner-next" className="p-2 border border-white/15 text-white hover:border-[#ff0033] transition-colors"><ChevronRight size={15} /></button>
          </div>
        </div>

        <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {events.map((e) => {
            const full = e.free_spots <= 0;
            const scarce = !full && e.free_spots <= 4;
            return (
              <div key={e.id} data-testid={`week-banner-event-${e.id}`}
                className="snap-start shrink-0 w-[250px] md:w-[280px] bg-black border border-white/10 hover:border-[#ff0033]/60 transition-colors p-4 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400">
                    {fmtDay(e.start, loc)} · <span className="text-white">{fmtTime(e.start)}</span>
                  </span>
                  {scarce && (
                    <span className="inline-flex items-center gap-1 font-mono-lab text-[9px] uppercase tracking-widest text-[#ff0033]">
                      <Flame size={11} /> {t(`Nur ${e.free_spots} frei`, `Only ${e.free_spots} left`)}
                    </span>
                  )}
                  {full && <span className="font-mono-lab text-[9px] uppercase tracking-widest text-neutral-500">{t("Ausgebucht", "Sold out")}</span>}
                </div>
                <p className="font-heading text-base md:text-lg uppercase tracking-tight text-white leading-tight mt-2 line-clamp-2" style={{ borderLeft: `3px solid ${e.color || "#ff0033"}`, paddingLeft: "8px" }}>
                  {e.course_name}{e.session_title ? ` · ${e.session_title}` : ""}
                </p>
                <div className="flex items-center justify-between mt-auto pt-3">
                  <span className="text-neutral-400 text-xs">{e.price ? `${e.price} €` : ""}</span>
                  <button
                    onClick={() => nav(`/kurse?course=${e.course_id}&session=${e.id}`)}
                    disabled={full}
                    data-testid={`week-banner-book-${e.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#ff0033] text-white font-heading text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#cc0029] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {t("Jetzt buchen", "Book now")} <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WeekCoursesBanner;
